import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useAjustes } from '../../Context/AjustesContext';

// Hook customizado para animar números
const useCountUp = (targetValue, duration = 2000) => {
	const [count, setCount] = useState(0);
	const startTimeRef = useRef(null);
	const animationFrameRef = useRef(null);

	useEffect(() => {
		if (targetValue === 0) {
			setCount(0);
			return;
		}

		startTimeRef.current = null;
		setCount(0);

		const animate = (timestamp) => {
			if (!startTimeRef.current) {
				startTimeRef.current = timestamp;
			}

			const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
			const currentCount = Math.floor(progress * targetValue);
			setCount(currentCount);

			if (progress < 1) {
				animationFrameRef.current = requestAnimationFrame(animate);
			} else {
				setCount(targetValue);
			}
		};

		animationFrameRef.current = requestAnimationFrame(animate);

		return () => {
			if (animationFrameRef.current) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, [targetValue, duration]);

	return count;
};

// Componente para mostrar as estatísticas do sistema para administradores
const SistemaInfo = ({ infoSistema }) => {
	const { ajustes } = useAjustes();

	// Animar os números
	const animatedAlunos = useCountUp(infoSistema.total_alunos || 0);
	const animatedCursos = useCountUp(infoSistema.total_cursos || 0);
	const animatedCertificados = useCountUp(infoSistema.total_certificados || 0);
	const animatedAulas = useCountUp(infoSistema.total_aulas_concluidas || 0);

    // Função para construir URL de imagem
    const getImageUrl = useCallback((path) => {
        if (!path) return null;
        if (path.startsWith('http')) {
            return path; // Already a full URL (external like Imgur)
        }
        return `${process.env.REACT_APP_API_URL}/${path}`;
    }, []);
	
	return (
		<div className="card-flag-dashboard rounded w-100 overflow-hidden mb-4 d-none d-md-block position-relative">
			<img 
				src={ajustes?.flag ? getImageUrl(ajustes.flag) : "./../assets/flag.png"} 
				alt="" 
				className="card-img w-100 img-banner" 
				onError={(e) => {
					e.target.onerror = null;
					e.target.src = "./../assets/flag.png";
				}}
			/>
			
			<div className="info-sistema position-absolute w-100 h-100 top-0 left-0 p-4" style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
				<h5 className="fw-semibold text-white mb-5">Informações do Sistema</h5>
				<div className="row">
					<div className="col-md-3 col-6 mb-3">
						<div className="d-flex flex-column align-items-center rounded bg-white p-3" style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
							<div className="fs-2 text-primary mb-2">
								<i className="bi bi-people-fill"></i>
							</div>
							<span className="fw-bold fs-4 text-primary">{animatedAlunos}</span>
							<span className="fw-medium fs-7 text-dark">Alunos</span>
						</div>
					</div>
					<div className="col-md-3 col-6 mb-3">
						<div className="d-flex flex-column align-items-center rounded bg-white p-3" style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
							<div className="fs-2 text-primary mb-2">
								<i className="bi bi-book-fill"></i>
							</div>
							<span className="fw-bold fs-4 text-primary">{animatedCursos}</span>
							<span className="fw-medium fs-7 text-dark">Cursos</span>
						</div>
					</div>
					<div className="col-md-3 col-6 mb-3">
						<div className="d-flex flex-column align-items-center rounded bg-white p-3" style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
							<div className="fs-2 text-primary mb-2">
								<i className="bi bi-award-fill"></i>
							</div>
							<span className="fw-bold fs-4 text-primary">{animatedCertificados}</span>
							<span className="fw-medium fs-7 text-dark">Certificados</span>
						</div>
					</div>
					<div className="col-md-3 col-6 mb-3">
						<div className="d-flex flex-column align-items-center rounded bg-white p-3" style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }}>
							<div className="fs-2 text-primary mb-2">
								<i className="bi bi-check2-circle"></i>
							</div>
							<span className="fw-bold fs-4 text-primary">{animatedAulas}</span>
							<span className="fw-medium fs-7 text-dark">Aulas Concluídas</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default SistemaInfo; 